/**
 * One 3Halves leg at a time per fan, across every tab of this browser.
 *
 * **Why a Web Lock and not a flag.** Two tabs resuming the same leave would both raise and both
 * confirm; a leave in one tab and a rejoin's cancel in another could interleave so that the cancel
 * runs first and the raise lands after it -- a rejoined fan with an erasure request standing. The
 * lock orders them. It cannot order two DEVICES; the server's repair on a superseded report covers
 * that (App\Loyalty\Membership\Saga::repair()).
 *
 * **The fallback is a lease in localStorage**, because sessionStorage is per tab and would order
 * nothing. It is weaker -- a check-then-set with no atomicity -- and it exists only for engines
 * without `navigator.locks`.
 *
 * Interactive legs WAIT (bounded by the caller's signal); a resume on page load does not, and simply
 * skips when another tab holds the lock.
 */

/** What `run()` answers when it did not get the lock and was told not to wait. */
export const SKIPPED = Object.freeze( { skipped: true } );

/**
 * @param {Object}       options            Wiring.
 * @param {Object|null}  options.locks      `navigator.locks`, or null.
 * @param {Function}     options.getStorage `() => Storage|null` -- localStorage.
 * @param {() => number} options.now        Epoch ms.
 * @param {string}       options.owner      This document's id, for the lease.
 * @param {number}       [options.leaseMs]  How long a fallback lease lasts.
 * @return {{run: Function}} The lock.
 */
export function createLegLock( { locks, getStorage, now, owner, leaseMs = 60000 } ) {
	/**
	 * @param {string}   sub           The fan.
	 * @param {Function} fn            The leg. May return a promise.
	 * @param {Object}   [opts]        Options.
	 * @param {boolean}  [opts.wait]   Queue for the lock rather than skip.
	 * @param {Object}   [opts.signal] Stops a wait.
	 * @return {Promise<*>} What `fn` resolved with, or SKIPPED.
	 */
	function run( sub, fn, { wait = false, signal } = {} ) {
		if ( locks && typeof locks.request === 'function' ) {
			const options = wait ? {} : { ifAvailable: true };

			if ( wait && signal ) {
				options.signal = signal;
			}

			return Promise.resolve()
				.then( () =>
					locks.request( `urc-membership:${ sub }`, options, lock =>
						lock === null ? SKIPPED : fn()
					)
				)
				.catch( error => {
					// An aborted wait rejects with AbortError: the caller's deadline passed while
					// another tab held the leg. That is a skip, not a failure of the leg.
					if ( error && error.name === 'AbortError' ) {
						return SKIPPED;
					}

					throw error;
				} );
		}

		return lease( sub, fn, wait );
	}

	/**
	 * @param {string}   sub  The fan.
	 * @param {Function} fn   The leg.
	 * @param {boolean}  wait Whether to proceed past somebody else's live lease.
	 * @return {Promise<*>} The leg's result, or SKIPPED.
	 */
	function lease( sub, fn, wait ) {
		const key = `urc.loyalty.membershipLease.${ sub }`;
		let store = null;

		try {
			store = getStorage() || null;
		} catch ( error ) {
			store = null;
		}

		const held = read( store, key );

		if ( held && held.owner !== owner && held.until > now() && ! wait ) {
			return Promise.resolve( SKIPPED );
		}

		write( store, key, { owner, until: now() + leaseMs } );

		return Promise.resolve()
			.then( fn )
			.finally( () => {
				const current = read( store, key );

				if ( current && current.owner === owner ) {
					remove( store, key );
				}
			} );
	}

	return { run };
}

/**
 * @param {Storage|null} store Storage.
 * @param {string}       key   Key.
 * @return {Object|null} The lease.
 */
function read( store, key ) {
	try {
		return store ? JSON.parse( store.getItem( key ) || 'null' ) : null;
	} catch ( error ) {
		return null;
	}
}

/**
 * @param {Storage|null} store Storage.
 * @param {string}       key   Key.
 * @param {Object}       value The lease.
 * @return {void}
 */
function write( store, key, value ) {
	try {
		if ( store ) {
			store.setItem( key, JSON.stringify( value ) );
		}
	} catch ( error ) {
		// Without storage there is no lease; the leg runs, and the server's re-judging of every
		// report is what stands behind it.
	}
}

/**
 * @param {Storage|null} store Storage.
 * @param {string}       key   Key.
 * @return {void}
 */
function remove( store, key ) {
	try {
		if ( store ) {
			store.removeItem( key );
		}
	} catch ( error ) {
		// As above.
	}
}
